import { Button } from "primereact/button";

import { IDraftRunDetail } from "../../../store/queries/contractModelDrafts";

interface IProps {
  run: IDraftRunDetail;
  regenerating: boolean;
  onRegenerate: () => void;
}

/**
 * "You have told the agent things it has not seen yet."
 *
 * The run is STALE when an instruction is queued or a document landed after the last generation.
 * Nothing fires automatically: a regeneration takes minutes, and she is usually adding several things
 * before she wants the model re-derived. She decides when.
 *
 * Note the copy deliberately says "re-derive the model", never "re-run this step". A regeneration IS
 * global. Implying otherwise would be a lie, and the moment a change landed on another step she would
 * rightly stop trusting the tool.
 */
function PendingChangesBar({ run, regenerating, onRegenerate }: IProps) {
  const inFlight =
    regenerating ||
    run.status === "queued" ||
    run.status === "running" ||
    run.status === "worker_returned";

  if (inFlight) {
    return (
      <div className="surface-100 border-round p-3 mb-3 flex align-items-center gap-3">
        <i className="pi pi-spin pi-spinner" />
        <div>
          <div className="font-semibold">Re-deriving the model…</div>
          {/* Non-blocking on purpose: the merge carries her verdicts forward by stable identity, so
              anything she confirms while this runs is NOT wasted. That property is what makes it safe
              to leave the wizard live rather than freezing it behind a spinner. */}
          <small className="text-color-secondary">
            This takes a few minutes. Keep reviewing — your confirmations will
            be carried over.
          </small>
        </div>
      </div>
    );
  }

  if (!run.isStale) return null;

  const bits: string[] = [];
  if (run.pendingInstructionCount > 0) {
    bits.push(
      `${run.pendingInstructionCount} instruction${
        run.pendingInstructionCount === 1 ? "" : "s"
      }`
    );
  }
  const docsAdded = run.isStale && run.pendingInstructionCount === 0;
  if (docsAdded) bits.push("a new document");

  return (
    <div className="border-round p-3 mb-3 border-1 border-orange-400 surface-50 flex justify-content-between align-items-center gap-3">
      <div>
        <div className="font-semibold">
          {bits.join(" and ")} not yet applied
        </div>
        <small className="text-color-secondary">
          The recommendations below were produced before this. Regenerate to
          have the agent take it into account.
        </small>
      </div>
      <Button
        label="Regenerate"
        icon="pi pi-refresh"
        onClick={onRegenerate}
        disabled={regenerating}
      />
    </div>
  );
}

export default PendingChangesBar;
